/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2024 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2024 - Finnish Meteorological Institute (FMI)
 * Copyright 2024 - The Norwegian Meteorological Institute (MET Norway)
 * */

import type {
  AirmetConfig,
  AirmetFromBackend,
  SigmetConfig,
  SigmetFromBackend,
} from '@opengeoweb/api';
import { getHeaders, handleResponse } from '@opengeoweb/api';
import type { Credentials } from '@opengeoweb/authentication';
import { sessionStorageProvider } from '@opengeoweb/shared';
import type {
  AirmetFromFrontend,
  AviationProduct,
  SigmetFromFrontend,
} from '@opengeoweb/sigmet-airmet';

const config = sessionStorageProvider.getConfig();

export const airmetBaseUrl = config.GW_AIRMET_BASE_URL || '';
export const sigmetBaseUrl = config.GW_SIGMET_BASE_URL || '';

// API
export const getAirmetList = async (
  auth: Credentials,
  params: Record<string, string> = {},
): Promise<AirmetFromBackend[]> => {
  const queryParams = new URLSearchParams(params);
  const url = `${airmetBaseUrl}/airmetlist${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
  const response = await fetch(url, {
    headers: getHeaders(auth),
  });
  const data = await handleResponse(response);

  return data as Promise<AirmetFromBackend[]>;
};

export const getSigmetList = async (
  auth: Credentials,
  params: Record<string, string> = {},
): Promise<SigmetFromBackend[]> => {
  const queryParams = new URLSearchParams(params);
  const url = `${sigmetBaseUrl}/sigmetlist${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;

  const response = await fetch(url, {
    headers: getHeaders(auth),
  });

  const data = await handleResponse(response);

  return data as Promise<SigmetFromBackend[]>;
};

export const getAirmetConfig = async (
  auth: Credentials,
): Promise<AirmetConfig> => {
  return fetch(`${airmetBaseUrl}/airmet-config`, {
    headers: getHeaders(auth),
  }).then(async (result) => result.json() as Promise<AirmetConfig>);
};

export const getSigmetConfig = async (
  auth: Credentials,
): Promise<SigmetConfig> => {
  return fetch(`${sigmetBaseUrl}/sigmet-config`, {
    headers: getHeaders(auth),
  }).then(async (result) => result.json() as Promise<SigmetConfig>);
};

export const getAirmetTAC = async (
  product: AviationProduct,
  auth?: Credentials,
): Promise<{ data: string }> => {
  return fetch(`${airmetBaseUrl}/airmet2tac`, {
    body: JSON.stringify(product),
    method: 'POST',
    headers: getHeaders(auth),
  }).then(async (result) => {
    if (result.status === 200) {
      const textData = await result.text();
      return { data: textData };
    }
    throw new Error(`Failed to fetch TAC data: ${result.statusText}`);
  });
};

export const getSigmetTAC = async (
  product: AviationProduct,
  auth?: Credentials,
): Promise<{ data: string }> => {
  return fetch(`${sigmetBaseUrl}/sigmet2tac`, {
    body: JSON.stringify(product),
    method: 'POST',
    headers: getHeaders(auth),
  }).then(async (result) => {
    if (result.status === 200) {
      const textData = await result.text();
      return { data: textData };
    }
    throw new Error(`Failed to fetch TAC data: ${result.statusText}`);
  });
};

// For cancel TAC (not normal TAC)
export const getAirmetCancelTAC = async (
  product: AviationProduct,
  auth?: Credentials,
): Promise<{ data: string }> => {
  return fetch(`${airmetBaseUrl}/airmet2cancel2tac`, {
    body: JSON.stringify(product),
    method: 'POST',
    headers: getHeaders(auth),
  }).then(async (result) => {
    if (result.status === 200) {
      const textData = await result.text();
      return { data: textData };
    }
    throw new Error(`Failed to fetch Cancel TAC data: ${result.statusText}`);
  });
};

export const getSigmetCancelTAC = async (
  product: AviationProduct,
  auth?: Credentials,
): Promise<{ data: string }> => {
  return fetch(`${sigmetBaseUrl}/sigmet2cancel2tac`, {
    body: JSON.stringify(product),
    method: 'POST',
    headers: getHeaders(auth),
  }).then(async (result) => {
    if (result.status === 200) {
      const textData = await result.text();
      return { data: textData };
    }
    throw new Error(`Failed to fetch Cancel TAC data: ${result.statusText}`);
  });
};

export const postSigmet = async (
  auth: Credentials,
  data: SigmetFromFrontend,
): Promise<SigmetFromBackend> =>
  fetch(`${sigmetBaseUrl}/sigmet`, {
    headers: getHeaders(auth),
    method: 'POST',
    body: JSON.stringify(data),
  }).then(handleResponse) as Promise<SigmetFromBackend>;

export const postAirmet = async (
  auth: Credentials,
  data: AirmetFromFrontend,
): Promise<AirmetFromBackend> =>
  fetch(`${airmetBaseUrl}/airmet`, {
    headers: getHeaders(auth),
    method: 'POST',
    body: JSON.stringify(data),
  }).then(handleResponse) as Promise<AirmetFromBackend>;
